import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, Mail, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_INFO = {
  awaiting_mandate: {
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/30',
    title: 'En attente de signature',
    desc: "Votre mandat SEPA n'a pas encore été signé.",
  },
  pending_approval: {
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/30',
    title: 'Demande en cours de validation',
    desc: 'Notre équipe va examiner votre demande sous 48h ouvrées.',
  },
  approved: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    title: 'Demande approuvée',
    desc: 'Votre commande est confirmée. Le 1er prélèvement va être effectué sous 5 à 7 jours ouvrés.',
  },
  rejected: {
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/30',
    title: 'Demande non validée',
    desc: 'Votre demande n\'a pas été approuvée. Consultez votre email pour le motif.',
  },
};

const SEPAPendingPage = () => {
  const { requestId } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/sepa/request/${requestId}`);
        if (!cancelled) setReq(r.data);
      } catch (e) {
        if (!cancelled) setReq({ status: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 8000); // poll every 8s
    return () => { cancelled = true; clearInterval(interval); };
  }, [requestId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        Chargement...
      </div>
    );
  }

  const info = STATUS_INFO[req?.status] || STATUS_INFO.pending_approval;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white" data-testid="sepa-pending-page">
      <div className="max-w-xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className={`inline-flex w-20 h-20 rounded-full ${info.bg} border ${info.border} items-center justify-center mb-4`}>
            {req?.status === 'approved' ? (
              <CheckCircle2 className={`w-10 h-10 ${info.color}`} />
            ) : (
              <Clock className={`w-10 h-10 ${info.color}`} />
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="sepa-pending-title">
            {info.title}
          </h1>
          <p className="text-white/60 mt-2">{info.desc}</p>
        </div>

        <div className={`rounded-xl border ${info.border} ${info.bg} p-6 mb-6`}>
          <div className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="text-white/50">Pack</div>
            <div className="text-white font-semibold text-right">{req?.pack_name || '-'}</div>
            <div className="text-white/50">IBAN</div>
            <div className="text-white text-right font-mono">**** **** **** {req?.iban_last4 || '????'}</div>
            <div className="text-white/50">Mensualité</div>
            <div className="text-white text-right">{(req?.monthly_price || 0).toFixed(2)} €/mois</div>
            <div className="text-white/50">1er prélèvement (au visa)</div>
            <div className="text-yellow-400 font-bold text-right">{(req?.first_charge_amount || 0).toFixed(2)} €</div>
          </div>
        </div>

        <div className="space-y-3 text-sm text-white/70">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-white/5 border border-white/10">
            <Mail className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-white mb-1">Un email de confirmation vient de vous être envoyé.</div>
              <div className="text-xs">
                Vérifiez votre boîte de réception (et vos spams). Vous recevrez un second email dès
                que notre équipe aura validé ou refusé votre demande.
              </div>
            </div>
          </div>

          {req?.status === 'approved' && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-400/5 border border-emerald-400/20">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                Conformément à la réglementation SEPA, vous recevrez une pré-notification email
                <strong className="text-white"> 3 jours avant chaque prélèvement</strong>. Les prélèvements
                récurrents commenceront 30 jours après la livraison effective du matériel.
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-10">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition" data-testid="sepa-back-home">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SEPAPendingPage;
